import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { motion } from 'framer-motion';
import { useSiteSettings } from '../../../context/SiteSettingsContext';

interface AdminSettings {
  id: string;
  legal_name: string;
  tax_id: string;
  address: string;
  city: string;
  postal_code: string;
  phone: string;
  bank_name: string;
  bank_iban: string;
  bank_bic: string;
}

export default function AdminSettings() {
  const [settings, setSettings] = useState<AdminSettings>({
    id: 'invoice_details',
    legal_name: 'Anthony Sánchez Atelier',
    tax_id: '46146123Z',
    address: 'Carrer del Mestre Nicolau, 19',
    city: 'Barcelona',
    postal_code: '08021',
    phone: '+34 932 000 000',
    bank_name: 'BANCO SABADELL',
    bank_iban: 'ES21 0081 1234 5678 9012 3456',
    bank_bic: 'BSABESBBXXX'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const { stripe: stripeConfig, updateStripeConfig } = useSiteSettings();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const { data, error } = await supabase
      .from('admin_settings')
      .select('*')
      .eq('id', 'invoice_details')
      .maybeSingle();

    if (data) {
      setSettings(data);
    }
    setLoading(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const { error } = await supabase
      .from('admin_settings')
      .upsert(settings);

    if (error) {
      setMessage({ type: 'error', text: 'Error al guardar: ' + error.message });
    } else {
      setMessage({ type: 'success', text: 'Tus datos fiscales se han actualizado correctamente.' });
      setTimeout(() => setMessage(null), 5000);
    }
    setSaving(false);
  };

  if (loading) return <div className="py-20 text-center"><i className="ri-loader-4-line animate-spin text-3xl text-brand-gray-200" /></div>;

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-12">
      <div className="text-left">
        <h2 className="font-serif text-brand-black text-2xl tracking-tight italic">Configuración del Atelier</h2>
        <p className="font-sans text-brand-gray-500 text-sm mt-1 font-medium">Gestiona la identidad y los datos de cobro oficiales.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {message && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }}
            className={`p-5 rounded-[24px] text-sm font-sans flex items-center gap-4 border shadow-sm ${
              message.type === 'success' ? 'bg-brand-sage/10 border-brand-sage/20 text-brand-black' : 'bg-red-50 border-red-100 text-red-600'
            }`}
          >
            <i className={message.type === 'success' ? "ri-checkbox-circle-fill text-xl text-brand-sage" : "ri-error-warning-fill text-xl text-red-400"} />
            <span className="font-bold">{message.text}</span>
          </motion.div>
        )}

        {/* Identidad Fiscal */}
        <div className="bg-white p-10 rounded-[40px] border border-brand-gray-100 shadow-sm space-y-8 text-left">
          <h3 className="font-sans text-[10px] uppercase tracking-[0.4em] text-brand-gray-400 font-bold border-b border-brand-gray-50 pb-4">Identidad Fiscal</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-500 ml-2 font-bold">Razón Social</label>
              <input 
                type="text" 
                required
                className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-brand-black/5 font-medium"
                value={settings.legal_name}
                onChange={(e) => setSettings({ ...settings, legal_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-500 ml-2 font-bold">NIF / CIF</label>
              <input 
                type="text" 
                required
                className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-brand-black/5 font-mono"
                value={settings.tax_id}
                onChange={(e) => setSettings({ ...settings, tax_id: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Contacto */}
        <div className="bg-white p-10 rounded-[40px] border border-brand-gray-100 shadow-sm space-y-8 text-left">
          <h3 className="font-sans text-[10px] uppercase tracking-[0.4em] text-brand-gray-400 font-bold border-b border-brand-gray-50 pb-4">Ubicación y Contacto</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-3 space-y-2">
              <label className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-500 ml-2 font-bold">Dirección del Atelier</label>
              <input 
                type="text" 
                required
                className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-sm font-medium"
                value={settings.address}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-500 ml-2 font-bold">Ciudad</label>
              <input 
                type="text" 
                required
                className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-sm font-medium"
                value={settings.city}
                onChange={(e) => setSettings({ ...settings, city: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-500 ml-2 font-bold">C.P.</label>
              <input 
                type="text" 
                required
                className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-sm font-medium"
                value={settings.postal_code}
                onChange={(e) => setSettings({ ...settings, postal_code: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-500 ml-2 font-bold">Teléfono</label>
              <input 
                type="text" 
                className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-sm font-medium"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Información Bancaria */}
        <div className="bg-white p-10 rounded-[40px] border border-brand-gray-100 shadow-sm space-y-8 text-left">
          <h3 className="font-sans text-[10px] uppercase tracking-[0.4em] text-brand-gray-400 font-bold border-b border-brand-gray-50 pb-4">Información de Pago</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-500 ml-2 font-bold">Entidad Bancaria</label>
              <input 
                type="text" 
                required
                className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-sm font-medium"
                value={settings.bank_name}
                onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-500 ml-2 font-bold">BIC / SWIFT</label>
              <input 
                type="text" 
                className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-sm font-mono"
                value={settings.bank_bic}
                onChange={(e) => setSettings({ ...settings, bank_bic: e.target.value })}
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <label className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-500 ml-2 font-bold">Número de Cuenta (IBAN)</label>
              <input 
                type="text" 
                required
                className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-sm font-mono tracking-widest"
                value={settings.bank_iban}
                onChange={(e) => setSettings({ ...settings, bank_iban: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button 
            type="submit" 
            disabled={saving}
            className="bg-brand-black text-white px-16 py-5 rounded-full font-sans text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-brand-gray-800 transition-all shadow-2xl shadow-black/20 flex items-center gap-4 disabled:opacity-50"
          >
            {saving ? <i className="ri-loader-4-line animate-spin text-lg" /> : <i className="ri-save-line text-lg" />}
            {saving ? 'Guardando cambios...' : 'Actualizar mis Datos'}
          </button>
        </div>
      </form>

      {/* Configuracion Stripe */}
      <div className="bg-white p-10 rounded-[40px] border border-brand-gray-100 shadow-sm space-y-8 text-left">
        <div className="flex items-center justify-between border-b border-brand-gray-50 pb-4">
          <h3 className="font-sans text-[10px] uppercase tracking-[0.4em] text-brand-gray-400 font-bold">Pasarela de Pagos (Stripe)</h3>
          <div className="flex items-center gap-3 bg-brand-gray-50 px-4 py-2 rounded-full">
            <span className="font-sans text-[10px] uppercase tracking-widest text-brand-gray-400 font-bold">Estado:</span>
            <span className={`font-sans text-[10px] uppercase tracking-widest font-bold ${stripeConfig.enabled ? 'text-brand-sage' : 'text-red-400'}`}>
              {stripeConfig.enabled ? 'Activo' : 'Desactivado'}
            </span>
          </div>
        </div>

        <div className="space-y-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 p-6 rounded-3xl bg-brand-gray-50/50 border border-brand-gray-100">
            <div className="max-w-md">
              <p className="font-sans text-brand-black text-sm font-bold">Habilitar Pagos con Tarjeta</p>
              <p className="font-sans text-brand-gray-500 text-xs mt-1 leading-relaxed">
                Permite a los clientes pagar sus facturas y compras en tienda mediante tarjeta de crédito a través de Stripe.
              </p>
            </div>
            <button
              onClick={() => updateStripeConfig({ ...stripeConfig, enabled: !stripeConfig.enabled })}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none ${
                stripeConfig.enabled ? 'bg-brand-sage' : 'bg-brand-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  stripeConfig.enabled ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2 ml-2">
              <i className="ri-percent-line text-brand-gray-400" />
              <label className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-500 font-bold">Recargo por Pago con Tarjeta (Facturas)</label>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-[200px]">
                <input 
                  type="number" 
                  className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-sm font-bold"
                  value={stripeConfig.invoiceSurchargePercentage}
                  onChange={(e) => updateStripeConfig({ ...stripeConfig, invoiceSurchargePercentage: Number(e.target.value) })}
                />
                <span className="absolute right-6 top-1/2 -translate-y-1/2 font-bold text-brand-gray-300">%</span>
              </div>
              <p className="font-sans text-[11px] text-brand-gray-400 italic">
                Este porcentaje se sumará al total de la factura si el cliente elige pagar con tarjeta.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
